import { useState, useEffect } from "react";
import { User, Check, X, Mail, Key, AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export function AccountSection() {
  const { user, isAuthenticated } = useAuth();
  const { 
    displayName, 
    username, 
    updateDisplayName, 
    updateUsername,
    isProfileSet,
    isUsernameSet 
  } = useProfile();
  
  const [editingName, setEditingName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Username state
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [savingUsername, setSavingUsername] = useState(false);
  
  // Email change state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (displayName) {
      setNewDisplayName(displayName);
    }
  }, [displayName]);

  useEffect(() => {
    if (username) {
      setNewUsername(username);
    }
  }, [username]);

  const handleSaveDisplayName = async () => {
    if (!newDisplayName.trim()) {
      toast.error("Display name cannot be empty");
      return;
    }
    
    setSaving(true);
    const success = await updateDisplayName(newDisplayName.trim());
    setSaving(false);
    
    if (success) {
      toast.success("Display name updated!");
      setEditingName(false);
    } else {
      toast.error("Failed to update display name");
    }
  };

  const handleCancelEdit = () => {
    setNewDisplayName(displayName || "");
    setEditingName(false);
  };

  const handleSaveUsername = async () => {
    if (!newUsername.trim()) {
      toast.error("Username cannot be empty");
      return;
    }
    
    setSavingUsername(true);
    const result = await updateUsername(newUsername.trim());
    setSavingUsername(false);
    
    if (result.success) {
      toast.success("Username updated!");
      setEditingUsername(false);
    } else {
      toast.error(result.error || "Failed to update username");
    }
  };

  const handleCancelUsernameEdit = () => {
    setNewUsername(username || "");
    setEditingUsername(false);
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim()) {
      toast.error("Please enter a new email address");
      return;
    }

    setEmailLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ 
        email: newEmail.trim() 
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Confirmation email sent to your new address. Please check both inboxes.");
        setEmailDialogOpen(false);
        setNewEmail("");
      }
    } catch (err) {
      toast.error("Failed to update email");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim()) {
      toast.error("Please enter a new password");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ 
        password: newPassword 
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success("Password updated successfully!");
        setPasswordDialogOpen(false);
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (err) {
      toast.error("Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };


  if (!isAuthenticated) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <User className="w-4 h-4" />
          Account
        </div>
        <p className="text-sm text-muted-foreground pl-6">
          Sign in to manage your account
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <User className="w-4 h-4" />
        Account
      </div>
      
      <div className="space-y-4 pl-6">
        {/* Username (unique identifier) */}
        <div className="space-y-2">
          <Label htmlFor="username" className="flex items-center gap-1">
            <AtSign className="w-3 h-3" />
            Username
          </Label>
          <p className="text-xs text-muted-foreground">
            Unique identifier for friends to find you
          </p>
          {editingUsername ? (
            <div className="flex gap-2">
              <Input
                id="username"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="yourname123"
                className="flex-1"
                autoFocus
                maxLength={20}
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={handleSaveUsername}
                disabled={savingUsername}
              >
                <Check className="w-4 h-4 text-primary" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleCancelUsernameEdit}
                disabled={savingUsername}
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {username ? `@${username}` : "Not set"}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingUsername(true)}
              >
                {username ? "Edit" : "Set"}
              </Button>
            </div>
          )}
        </div>

        {/* Display Name */}
        <div className="space-y-2">
          <Label htmlFor="display-name">Display Name</Label>
          <p className="text-xs text-muted-foreground">
            Shown on leaderboards and in rounds
          </p>
          {editingName ? (
            <div className="flex gap-2">
              <Input
                id="display-name"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Enter display name"
                className="flex-1"
                autoFocus
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={handleSaveDisplayName}
                disabled={saving}
              >
                <Check className="w-4 h-4 text-primary" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{displayName || "Not set"}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditingName(true)}
              >
                Edit
              </Button>
            </div>
          )}
        </div>

        
        {/* Email */}
        <div className="space-y-2">
          <Label>Email</Label>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Mail className="w-3 h-3 mr-1" />
                  Change
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Email</DialogTitle>
                  <DialogDescription>
                    Enter your new email address. We'll send confirmation links to both your old and new addresses.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-email">New Email</Label>
                    <Input
                      id="new-email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="newemail@example.com"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleChangeEmail} disabled={emailLoading}>
                    {emailLoading ? "Sending..." : "Send Confirmation"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label>Password</Label>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">••••••••</p>
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Key className="w-3 h-3 mr-1" />
                  Change
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Change Password</DialogTitle>
                  <DialogDescription>
                    Enter your new password. Make sure it's at least 6 characters long.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleChangePassword} disabled={passwordLoading}>
                    {passwordLoading ? "Updating..." : "Update Password"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
